/**
 * Alert — inline banner for status messages.
 *
 * Used for error display on auth forms, optimistic-rollback notices, and
 * other in-page messaging. Tinted background + matching text + optional icon.
 */
import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, CircleAlert, Info } from 'lucide-react';
import { cn } from '@/lib/cn';

export type AlertTone = 'success' | 'warning' | 'danger' | 'info';

export interface AlertProps {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  className?: string;
}

const toneClasses: Record<AlertTone, string> = {
  success: 'bg-status-success-bg text-status-success border-status-success/20',
  warning: 'bg-status-warning-bg text-status-warning border-status-warning/20',
  danger: 'bg-status-danger-bg text-status-danger border-status-danger/20',
  info: 'bg-status-info-bg text-status-info border-status-info/20',
};

const toneIcons: Record<AlertTone, ReactNode> = {
  success: <CheckCircle2 className="h-4 w-4 shrink-0" strokeWidth={1.5} />,
  warning: <AlertTriangle className="h-4 w-4 shrink-0" strokeWidth={1.5} />,
  danger: <CircleAlert className="h-4 w-4 shrink-0" strokeWidth={1.5} />,
  info: <Info className="h-4 w-4 shrink-0" strokeWidth={1.5} />,
};

export function Alert({ tone = 'info', title, children, className }: AlertProps) {
  return (
    <div
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-sm',
        toneClasses[tone],
        className,
      )}
    >
      {toneIcons[tone]}
      <div className="flex-1">
        {title ? <div className="font-medium">{title}</div> : null}
        {children ? <div className={cn(title ? 'mt-0.5' : '')}>{children}</div> : null}
      </div>
    </div>
  );
}
